import { useState, useCallback } from "react";
import { enqueueToaster } from "@deere/isg.component-library";

interface UseApiCallOptions {
  successMessage?: string;
  successTitle?: string;
  errorAsToast?: boolean;
}

interface ExecuteOptions {
  successMessage?: string;
  successTitle?: string;
  errorAsToast?: boolean;
}

interface UseApiCallReturn {
  execute: (apiFn: () => Promise<Response>, executeOptions?: ExecuteOptions) => Promise<boolean>;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
}

function useApiCall(options?: UseApiCallOptions): UseApiCallReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const execute = useCallback(
    async (apiFn: () => Promise<Response>, executeOptions?: ExecuteOptions): Promise<boolean> => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiFn();

        // Session expired — redirect to login
        if (response.status === 401) {
          enqueueToaster("Session expired — please log in again", "Session Expired", {
            variant: "error",
          });
          setTimeout(() => {
            window.location.assign("/API/auth/login");
          }, 1500);
          return false;
        }

        if (!response.ok) {
          let errorMsg = `Request failed (${response.status})`;
          try {
            const errorBody = await response.json();
            if (errorBody?.message) {
              errorMsg = errorBody.message;
            }
          } catch {}
          const err = new Error(errorMsg);
          (err as any).status = response.status;
          throw err;
        }

        const msg = executeOptions?.successMessage ?? options?.successMessage;
        const title = executeOptions?.successTitle ?? options?.successTitle ?? "Success";
        if (msg) {
          enqueueToaster(msg, title, { variant: "success" });
        }
        return true;
      } catch (err: any) {
        const msg =
          err?.message === "Failed to fetch"
            ? "Cannot connect to server. Check your connection."
            : err?.message || "Something went wrong. Please try again.";
        const showToast = executeOptions?.errorAsToast ?? options?.errorAsToast;
        if (showToast) {
          if (err?.status === 409) {
            enqueueToaster(msg, "Machine Busy", { variant: "warning" });
          } else {
            enqueueToaster(msg, "Error", { variant: "error" });
          }
        } else {
          setError(msg);
        }
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [options?.successMessage, options?.successTitle],
  );

  const clearError = useCallback(() => setError(null), []);

  return { execute, isLoading, error, clearError };
}

export default useApiCall;
